import { useState, useRef, useEffect } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "@/hooks/useAppSelector";
import { authAPI } from "@/services/api";
import {
  Activity,
  BookOpen,
  Bot,
  Building2,
  Cpu,
  Clock,
  CreditCard,
  Globe,
  Layout,
  LayoutDashboard,
  Mail,
  MailCheck,
  MessageCircle,
  Package,
  Receipt,
  Server,
  ServerCogIcon,
  ShieldAlert,
  Shapes,
  SquareArrowOutUpRight,
  Tags,
  ToggleLeft,
  Plug,
  Users,
  UserPlus,
  Wrench,
  DollarSign,
  RotateCcw,
  Radar,
  Gift,
  Database,
  LifeBuoy,
  Newspaper,
} from "lucide-react";
import { ROUTES } from "@/lib/constants";

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const navigation = [
    {
      name: "Dashboard",
      href: "/admin",
      icon: <LayoutDashboard />,
    },
    {
      name: "Organizations",
      href: "/admin/organizations",
      icon: <Building2 />,
    },
    {
      name: "Users",
      href: "/admin/users",
      icon: <Users />,
    },
    {
      name: "Support",
      href: "/admin/support",
      icon: <LifeBuoy />,
    },
    {
      name: "Waitlist",
      href: "/admin/waitlist",
      icon: <UserPlus />,
    },
    {
      name: "Referrals",
      href: "/admin/referrals",
      icon: <Gift />,
    },
    {
      name: "Token Usage",
      href: "/admin/token-usage",
      icon: <CreditCard />,
    },
    {
      name: "Cost Events",
      href: "/admin/cost-events",
      icon: <Receipt />,
    },
    {
      name: "Connectors",
      href: "/admin/connectors",
      icon: <Server />,
    },
    {
      name: "Connector Resync",
      href: "/admin/connector-resync",
      icon: <RotateCcw />,
    },
    {
      name: "Providers",
      href: ROUTES.ADMIN_PROVIDERS,
      icon: <Package />,
    },
    {
      name: "Models",
      href: ROUTES.ADMIN_MODELS,
      icon: <Shapes />,
    },
    {
      name: "Product Models",
      href: "/admin/product-models",
      icon: <Tags />,
    },
    {
      name: "Model Registry",
      href: ROUTES.ADMIN_MODEL_REGISTRY,
      icon: <Cpu />,
    },
    {
      name: "Routing Health",
      href: "/admin/routing-health",
      icon: <Radar />,
    },
    {
      name: "Model Pricing",
      href: "/admin/model-pricing",
      icon: <DollarSign />,
    },
    { name: "Tools", href: "/admin/tools", icon: <Wrench /> },
    { name: "Blog", href: "/admin/blog", icon: <Newspaper /> },
    // { name: 'System Logs', href: '/admin/logs', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' }
    {
      name: "Prompts",
      href: "/admin/prompts",
      icon: <MessageCircle />,
    },
    { name: "Agents", href: "/admin/agents", icon: <Bot /> },
    { name: "Native Agents", href: "/admin/native-agents", icon: <Bot /> },
    { name: "Abilities", href: "/admin/abilities", icon: <BookOpen /> },
    { name: "Env", href: "/admin/env", icon: <ServerCogIcon /> },
    { name: "Email Assistant", href: "/admin/email-assistant", icon: <Mail /> },
    {
      name: "Communications",
      href: "/admin/communications",
      icon: <MailCheck />,
    },
    { name: "Rate Limits", href: "/admin/rate-limits", icon: <ShieldAlert /> },
    {
      name: "Ingestion Scope",
      href: "/admin/ingestion-scope",
      icon: <Database />,
    },
    { name: "Agent Config", href: "/admin/model-config", icon: <Globe /> },
    { name: "Workers", href: "/admin/workers", icon: <Activity /> },
    { name: "GPU Monitoring", href: "/admin/gpu-monitoring", icon: <Cpu /> },
    { name: "Operation Logs", href: "/admin/operation-logs", icon: <Clock /> },
    { name: "Slide Traces", href: "/admin/slide-traces", icon: <Activity /> },
    {
      name: "Feature Flags",
      href: "/admin/feature-flags",
      icon: <ToggleLeft />,
    },
    {
      name: "Connector Flags",
      href: "/admin/connector-flags",
      icon: <Plug />,
    },
    {
      name: "Sidebar Config",
      href: "/admin/sidebar-config",
      icon: <Layout />,
    },
    { name: "Plan Config", href: "/admin/plan-config", icon: <CreditCard /> },
  ];

  // Close user menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setUserMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await authAPI.logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
      // Still navigate to login even if logout API fails
      navigate("/login");
    } finally {
      setUserMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex h-screen overflow-hidden">
        {/* Sidebar */}
        <div
          className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform flex flex-col ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          } transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 lg:z-auto`}
        >
          <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
            <img src="/libraai.png" height={40} width={40} />

            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <nav className="mt-6 px-3 flex-1 overflow-y-auto">
            <div className="space-y-1">
              {navigation.map((item) => {
                const isActive =
                  location.pathname === item.href ||
                  (item.href !== "/admin" &&
                    location.pathname.startsWith(`${item.href}/`));
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                      isActive
                        ? "bg-blue-100 text-blue-700 border-r-2 border-blue-700"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <svg
                      className={`mr-3 h-5 w-5 shrink-0 ${
                        isActive
                          ? "text-blue-500"
                          : "text-gray-400 group-hover:text-gray-500"
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      {item.icon}
                    </svg>
                    {item.name}
                  </Link>
                );
              })}
            </div>
          </nav>
          <div className="px-3 py-4">
            <Link
              to="/service/analytics"
              className="group flex items-center px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-md transition-colors duration-200"
            >
              Service API
              <SquareArrowOutUpRight className="h-4 w-4 ml-2" />
            </Link>
          </div>
        </div>

        {/* Main content area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Top bar */}
          <div className="shrink-0 flex items-center justify-between h-16 bg-white border-b border-gray-200 px-6 shadow-sm">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            </div>

            <div className="flex items-center space-x-4">
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-2 p-1 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  {user?.avatar ? (
                    <img
                      src={user.avatar}
                      alt={user.name}
                      className="w-8 h-8 rounded-full object-cover border-2 border-gray-200"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                      <span className="text-white text-sm font-medium">
                        {user?.name?.charAt(0) || "A"}
                      </span>
                    </div>
                  )}
                  <svg
                    className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                      userMenuOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {/* User dropdown menu */}
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <div className="text-sm font-medium text-gray-900">
                        {user?.name || "Admin"}
                      </div>
                      <div className="text-sm text-gray-500">
                        {user?.role || "User"}
                      </div>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition-colors duration-200 flex items-center space-x-2"
                    >
                      <svg
                        className="w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                      </svg>
                      <span>Sign out</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Page content with max-width constraint */}
          <main className="flex-1 overflow-y-auto bg-gray-50">
            <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 min-h-full">
                <div className="p-6">
                  {/* {children} */}
                  <Outlet />
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
